/**
 * Project-scoped capability helpers.
 *
 * The backend resolves the requesting user's capabilities per project in
 * `ProjectType.viewerCapabilities`, read straight from
 * `backend/app/core/access_policy.py` — the same table the mutation permission
 * gates enforce. This module is the frontend's only way to read them.
 *
 * Do NOT reimplement the role → capability mapping client-side. The gates this
 * replaced collapsed all five `ProjectRoleType`s into "user holds some role on
 * this project", which showed Työnjohtaja-tier users buttons the backend then
 * rejected with a 403. Capability names are strings rather than a generated
 * enum so that a new backend capability cannot break the build.
 */

/** Capability keys that can appear in `viewerCapabilities`. */
export const ProjectCapability = {
  VIEW_PROJECT: "view_project",
  EDIT_ALL_TASKS: "edit_all_tasks",
  EDIT_OWN_TASKS: "edit_own_tasks",
  COMMENT: "comment",
  MANAGE_SITE_STRUCTURE: "manage_site_structure",
  MANAGE_PROJECT_ROLES: "manage_project_roles",
  MANAGE_RESPONSIBILITIES: "manage_responsibilities",
  MANAGE_RESPONSIBILITY_MATRIX: "manage_responsibility_matrix",
  CONGRID_EDIT: "congrid_edit",
  TRIGGER_SYNC: "trigger_sync",
  RUN_MINI_INIT: "run_mini_init",
  DELETE_PROJECT: "delete_project",
} as const;

export type ProjectCapabilityKey =
  (typeof ProjectCapability)[keyof typeof ProjectCapability];

/** Minimal shape needed — anything carrying a `viewerCapabilities` list. */
export interface HasViewerCapabilities {
  viewerCapabilities?: string[] | null;
}

/**
 * Whether the viewer holds `capability` on `project`.
 *
 * Fails closed: a null/undefined project, or one whose query omitted
 * `viewerCapabilities`, yields `false`. Hiding a control the user could in
 * principle use is recoverable; showing one the backend will reject is not.
 */
export function hasCapability(
  project: HasViewerCapabilities | null | undefined,
  capability: ProjectCapabilityKey,
): boolean {
  return !!project?.viewerCapabilities?.includes(capability);
}
